// 分析者總表（SPEC #vms-web-laih-l1，LAIH-01 的 L1 子集）：主體底下每台攝影機目前由誰分析——
//   本地（AI 運算提供者：AI box / NPU NVR / Kinio Station PC 版；顯示提供者名稱與指派狀態，提供者離線 = 本地 AI 離線）
//   ／雲端 AI（訂閱有效、同意有效、沒排除）／本地 + 雲端（主體打開「本地與雲端都分析」）／無。
// 指派到本地的攝影機雲端預設跳過（平台 vision 主體清單把它們併進 excluded_refs）；開關只給 org_admin / home owner，打開要重新驗證。
// 資料全由既有 API 組（各家戶 …/vms/ai/providers、…/ai/consent、…/ai/usage），不另開讀取 API。
import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { aiApi, type AiConsent, type AiEntitlement, type AiProvider, type AiAssignment } from '../../lib/planeApi';
import { fullName, type VmsDevice } from '../../lib/devices';

export type Analyzer = 'local' | 'cloud' | 'both' | 'none' | 'unknown';

/** 一台攝影機的分析者（純函式，給畫面與單元測試）。 */
export function analyzerOf(
  cam: Pick<VmsDevice, 'aid'>,
  local: { provider: AiProvider; a: AiAssignment } | undefined,
  consent: AiConsent | null,
  consentCurrent: boolean,
  ai: Pick<AiEntitlement, 'active'> | undefined,
): Analyzer {
  const cloudBase = !!ai?.active && !!consent && consentCurrent && !consent.excluded_refs.includes(cam.aid);
  if (local) return cloudBase && consent?.analyze_local_too ? 'both' : 'local';
  return cloudBase ? 'cloud' : 'none';
}

export function AnalyzerTable({
  cams,
  tree,
  homes,
  consent,
  consentCurrent,
  ai,
  cloudError,
  canToggle,
  busy,
  onToggle,
}: {
  cams: VmsDevice[];
  tree: VmsDevice[];
  homes: { api: string; homeId: string }[];
  consent: AiConsent | null;
  consentCurrent: boolean;
  ai: AiEntitlement | undefined;
  /** 雲端 AI 的同意或用量讀不到：雲端那一半不能判斷 */
  cloudError: boolean;
  canToggle: boolean;
  busy: boolean;
  onToggle: (on: boolean) => void;
}) {
  const { t } = useTranslation();
  const qs = useQueries({
    queries: homes.map((h) => ({ queryKey: ['ai-providers', h.homeId], queryFn: () => aiApi.providers(h.api, h.homeId), refetchInterval: 5000 })),
  });
  const localOf = new Map<string, { provider: AiProvider; a: AiAssignment }>();
  // 本地 AI 狀態還沒拿到 / 拿不到的家戶：那些攝影機標「無法確認」，不當成沒有本地指派（不然會誤標成雲端 / 無）
  const pending = new Set<string>();
  const failed = new Set<string>();
  qs.forEach((q, i) => {
    if (q.isLoading) pending.add(homes[i].homeId);
    else if (q.error && !q.data) failed.add(homes[i].homeId);
    for (const p of q.data ?? []) for (const a of p.assignments) localOf.set(`${homes[i].homeId}/${a.device_ref}`, { provider: p, a });
  });
  if (cams.length === 0) return null;
  return (
    <div className="stack" data-testid="ai-analyzers">
      <h3 className="section-title" style={{ fontSize: 15 }}>
        {t('aiLocal.title')}
      </h3>
      <p className="muted small">{t('aiLocal.help')}</p>
      {consent && consentCurrent && (
        <label>
          <input
            type="checkbox"
            checked={!!consent.analyze_local_too}
            disabled={!canToggle || busy}
            onChange={(e) => onToggle(e.target.checked)}
            data-testid="ai-local-too"
          />{' '}
          {t('aiLocal.local_too')} <span className="muted small">{t('aiLocal.local_too_help')}</span>
        </label>
      )}
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>{t('aiLocal.col_camera')}</th>
              <th>{t('aiLocal.col_analyzer')}</th>
              <th>{t('aiLocal.col_detail')}</th>
            </tr>
          </thead>
          <tbody>
            {cams.map((cam) => {
              const local = localOf.get(`${cam.homeId}/${cam.aid}`);
              // 沒問到本地 AI 狀態的家戶（分享進來的 / 範圍裡找不到）與雲端狀態讀不到的，都不能判斷
              const notQueried = !homes.some((h) => h.homeId === cam.homeId);
              const unknown = pending.has(cam.homeId) || failed.has(cam.homeId) || notQueried || cloudError;
              const who: Analyzer = unknown ? 'unknown' : analyzerOf(cam, local, consent, consentCurrent, ai);
              let detail = '';
              if (unknown)
                detail = pending.has(cam.homeId)
                  ? t('common.loading')
                  : failed.has(cam.homeId)
                    ? t('aiLocal.providers_failed')
                    : notQueried
                      ? t('aiLocal.not_queried')
                      : t('aiLocal.cloud_failed');
              else if (local) {
                detail = local.provider.online
                  ? `${local.provider.name || local.provider.device_ref} · ${t(`aiLocal.status_${local.a.status ?? 'pending'}`, { defaultValue: t('aiLocal.status_other', { status: local.a.status ?? '' }) })}${local.a.status === 'degraded' && local.a.fps ? ` (${local.a.fps} fps)` : ''}`
                  : `${local.provider.name || local.provider.device_ref} · ${t('aiLocal.offline')}`;
              } else if (who === 'none' && consent?.excluded_refs.includes(cam.aid)) detail = t('aiLocal.excluded');
              return (
                <tr key={cam.key} data-analyzer-row={cam.aid} data-analyzer={who} data-local-offline={local && !local.provider.online ? '1' : undefined}>
                  <td>{fullName(cam, tree)}</td>
                  <td>
                    <span className={`badge ${who === 'none' || who === 'unknown' ? '' : 'badge-ok'}`}>{t(`aiLocal.who_${who}`)}</span>
                  </td>
                  <td className="small">{detail}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
